// Must stay the first import: it clears the cached Cognito session when the dev
// server's auth mode changed (start / start:sso / start:sky), and ES imports are
// hoisted, so anything imported above it would read auth state from the old mode.
import "~/login/dev-auth-mode";

import "./reset.css";
import "./theme/css-variables/master-variables.css";

import React from "react";
import PropTypes from "prop-types";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Route, Switch, withRouter } from "react-router-dom";

import { Amplify } from "aws-amplify";
import { urlBase, urlJoin, isSkyUrl, LocalStorageHelpers } from "@ai360/core";

// Configure Amplify early from cached config (supports session check on page refresh)
try {
    const cachedConfig = LocalStorageHelpers.get(LocalStorageHelpers.COGNITO_CONFIG);
    if (cachedConfig) {
        const cfg = JSON.parse(cachedConfig);
        if (cfg.userPoolId && cfg.userPoolClientId) {
            Amplify.configure({
                Auth: {
                    Cognito: {
                        userPoolId: cfg.userPoolId,
                        userPoolClientId: cfg.userPoolClientId,
                        region: cfg.region,
                    },
                },
            });
        }
    }
} catch {
    // Config not available yet — will be configured in fetchCognitoConfig
}
import store from "./store/store";
import { ConnectedIntlProvider } from "./intl-provider";
import { RootComponent } from "./root-component";
import { getUser, LoginPage, GetMobileApp, EnrollmentPage } from "./login";
import { ReceiveAuthentication } from "~/authentication/receive-authentication";
import { MyAgDataRegistration } from "~/login/components/myagdata-registration";
import { initializeApp } from "firebase/app";
import { initializePerformance } from "firebase/performance";
import { initializeAnalytics } from "firebase/analytics";
import { MyAgDataGrowerReview } from "~/login/components/myagdata-grower-review";
import { reportRecoverableError } from "~/notifications/actions";
import { startInteractionTracking } from "~/notifications/last-interaction";

let appEntry = "";

export const EnsureLoggedIn = ({ history }) => {
    if (!getUser(store.getState())) {
        appEntry = history.location.pathname;
        if (isSkyUrl) {
            const skyUserInfo = JSON.parse(LocalStorageHelpers.get("USER_INFO_KEY"));
            if (skyUserInfo == null) {
                window.location.href = window.location.origin;
                return null;
            }
        }

        history.replace(urlJoin(urlBase, "login"));
        return null;
    }

    const enteredFromMobile = appEntry.toLowerCase().indexOf("getmobileapp") > -1;
    const onMobilePage = history.location.pathname.toLowerCase().indexOf("getmobileapp") > -1;

    if (enteredFromMobile && !onMobilePage) {
        history.replace(urlJoin(urlBase, "GetMobileApp"));
        return null;
    } else if (onMobilePage) {
        appEntry = "";
        return <GetMobileApp />;
    }

    return <RootComponent />;
};

EnsureLoggedIn.propTypes = {
    history: PropTypes.any.isRequired,
};

try {
    const firebaseIsConfigured =
        window.process_env.FB_API_KEY && window.process_env.FB_API_KEY !== "%FB_API_KEY%";
    if (firebaseIsConfigured) {
        const firebaseConfig = {
            apiKey: window.process_env.FB_API_KEY,
            authDomain: window.process_env.FB_AUTH_DOMAIN,
            databaseURL: window.process_env.FB_DB_URL,
            projectId: window.process_env.FB_PROJECT_ID,
            storageBucket: window.process_env.FB_STORAGE_BUCKET,
            messagingSenderId: window.process_env.FB_MESSAGING_SENDER_ID,
            appId: window.process_env.FB_APP_ID,
            measurementId: window.process_env.FB_MEASUREMENT_ID,
        };

        const firebaseApp = initializeApp(firebaseConfig);
        initializePerformance(firebaseApp);
        initializeAnalytics(firebaseApp);
    } else {
        console.warn("Firebase is not configured");
    }
} catch (err) {
    console.error("Error initializing Firebase: ", err);
}

startInteractionTracking();

// Keeps recovered errors off window.reportError, which opens the blocking error dialog.
// Deferred because this runs inside React's commit.
const onRecoverableError = (error, errorInfo) => {
    setTimeout(() => store.dispatch(reportRecoverableError(error, errorInfo?.componentStack)), 0);
};

const root = createRoot(document.getElementById("root"), { onRecoverableError });
root.render(
    <Provider store={store}>
        <ConnectedIntlProvider>
            <Router>
                <Switch>
                    <Route exact path={urlBase} component={withRouter(EnsureLoggedIn)} />
                    <Route path={"*/login"} component={LoginPage} />
                    <Route path={"*/getmobileapp"} component={withRouter(EnsureLoggedIn)} />
                    <Route path={"*/enrollment"} component={EnrollmentPage} />
                    <Route path={"*/myagdata"} component={MyAgDataRegistration} />
                    <Route path={"*/myagdata-review"} component={MyAgDataGrowerReview} />
                    <Route path={"*/auth/callback"} component={ReceiveAuthentication} />
                    <Route path={"*"} component={withRouter(EnsureLoggedIn)} />
                </Switch>
            </Router>
        </ConnectedIntlProvider>
    </Provider>
);
